import { type UseMutationOptions, useMutation } from "@tanstack/react-query";
import {
	type ApiError,
	type ApiRequestOptions,
	apiRequest,
} from "@/lib/api-client";
import { useAuth } from "@/lib/auth-ui";

type UseApiMutationConfig<TData, TVariables, TError = ApiError> = {
	getRequest: (variables: TVariables) => {
		path: string;
	} & Omit<ApiRequestOptions, "token">;
	mutationOptions?: UseMutationOptions<TData, TError, TVariables, unknown>;
};

export function useApiMutation<TData, TVariables = void, TError = ApiError>({
	getRequest,
	mutationOptions,
}: UseApiMutationConfig<TData, TVariables, TError>) {
	const { getToken } = useAuth();

	return useMutation<TData, TError, TVariables>({
		mutationFn: async (variables) => {
			const { path, ...request } = getRequest(variables);
			const token = await getToken();

			return apiRequest<TData>(path, {
				...request,
				token: token ?? null,
				credentials: token ? "omit" : request.credentials,
			});
		},
		...mutationOptions,
	});
}
